"use client";
import {
  StyledCheckboxContainer,
  StyledCheckboxLabel,
  getCheckboxProps,
  StyledCheckbox,
  Divider,
  Button,
  Input,
  FormGroup,
  Label,
} from "cy-fe";
import { HeaderText } from "cy-remittance/components/HeaderText";
import { BodyContainer } from "cy-remittance/components/layouts";
import * as StepWizard from "cy-ui/components/StepWizard";
import { useController, useForm } from "react-hook-form";

export interface MarketingInfoInputs {
  moneyMarketProducts: boolean;
  sms: boolean;
  whatsApp: boolean;
  referralCode?: string;
}
export function MarketingInfo({
  backUrl,
  onSubmit,
  initial,
  initialReferralCode,
}: {
  backUrl: string;
  initial: Record<string, boolean> | undefined;
  initialReferralCode?: string;
  onSubmit: (args: {
    marketingConsents: Record<string, boolean>;
    referralCode?: string;
  }) => void;
}) {
  const typedInitial: MarketingInfoInputs = initial
    ? {
        moneyMarketProducts: !!initial?.moneyMarketProducts,
        sms: !!initial?.sms,
        whatsApp: !!initial?.whatsApp,
        referralCode: initialReferralCode || "",
      }
    : // Requirement is to default to true, not false
      {
        moneyMarketProducts: true,
        sms: true,
        whatsApp: true,
        referralCode: initialReferralCode || "",
      };
  const form = useForm<MarketingInfoInputs>({ defaultValues: typedInitial });

  function handleSubmit(values: MarketingInfoInputs) {
    const consents: Record<string, boolean> = {
      moneyMarketProducts: !!values.moneyMarketProducts,
      sms: !!values.sms,
      whatsApp: !!values.whatsApp,
    };
    onSubmit({
      marketingConsents: consents,
      referralCode: values.referralCode?.trim() || undefined,
    });
  }

  const sms = useController({
    control: form.control,
    name: "sms",
  });
  const whatsApp = useController({
    control: form.control,
    name: "whatsApp",
  });

  const moneyMarketProducts = useController({
    control: form.control,
    name: "moneyMarketProducts",
  });

  const referralCode = useController({
    control: form.control,
    name: "referralCode",
  });
  return (
    <StepWizard.StepPageLayout
      backUrl={backUrl}
      headerRender={() => (
        <HeaderText className="font-bold">Promotions</HeaderText>
      )}
      subHeaderRender={() => <>Check out our promotions</>}
    >
      <form
        className="w-full space-y-8"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <BodyContainer className="mt-10">
          <StyledCheckboxContainer className="flex gap-3">
            <StyledCheckbox
              {...getCheckboxProps(moneyMarketProducts)}
              className="mt-[0.3rem] flex-shrink-0"
            />
            <StyledCheckboxLabel className="text-base font-normal">
              I agree to receive marketing information with regards to other{" "}
              <span className="font-semibold">Money Market Products</span>.
            </StyledCheckboxLabel>
          </StyledCheckboxContainer>
          <Divider className="!mb-10" />
          <p>I want to receive the above marketing information via:</p>
          <div className="mx-5 flex items-center justify-between">
            <StyledCheckboxContainer className="flex items-center gap-3">
              <StyledCheckbox
                {...getCheckboxProps(sms)}
                className="flex-shrink-0"
              />
              <StyledCheckboxLabel className="text-base font-normal">
                SMS
              </StyledCheckboxLabel>
            </StyledCheckboxContainer>
            <StyledCheckboxContainer className="flex items-center gap-3">
              <StyledCheckbox
                {...getCheckboxProps(whatsApp)}
                className="flex-shrink-0"
              />
              <StyledCheckboxLabel className="text-base font-normal">
                WhatsApp
              </StyledCheckboxLabel>
            </StyledCheckboxContainer>
          </div>
          <div className="mt-8">
            <FormGroup
              controller={{ control: form.control, name: "referralCode" }}
            >
              <Label>Referral code (optional)</Label>
              <Input
                vSize="lg"
                placeholder="Enter referral code"
                {...form.register("referralCode")}
              />
            </FormGroup>
          </div>
          <Button
            type="submit"
            className="mt-10 w-full"
            loading={form.formState.isSubmitting}
            vSize="lg"
          >
            Next
          </Button>
        </BodyContainer>
      </form>
    </StepWizard.StepPageLayout>
  );
}
